import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useRef } from 'react';

import { Button } from '@domcrm/embed-ui';

import type { ScheduleSlotListItem } from '../widget-api/schedule-slots';
import type { PDTransferNotice } from '../widget-api/pd-transfer';
import { formatCancellationLeadMinutes, formatPaymentTTLMinutes } from '../widget-lib/payment-ttl';
import type { ForSlotSubscription } from '../widget-api/subscriptions';
import { LegalAgreementHtmlBlock } from './legal-agreement-html-block';

function formatDay(iso: string): string {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString('ru-RU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  } catch {
    return iso;
  }
}

function formatValidUntil(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return iso.slice(0, 10);
  }
}

type BookingConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  slot: ScheduleSlotListItem;
  paymentMode: 'online' | 'subscription' | 'cash';
  selectedSubscription: ForSlotSubscription | null;
  slotSubs: ForSlotSubscription[];
  cashAtStudioEnabled: boolean;
  serviceNeedsPayment: boolean;
  /** Effective org payment window; default 15 minutes. */
  paymentTTLMinutes?: number;
  addonsTotal?: number;
  addonsCount?: number;
  onSelectPaymentMode?: (mode: 'online' | 'subscription' | 'cash') => void;
  onSelectSubscription?: (subId: string) => void;
  /** Ф8 (ТЗ 5.14 п.4): уведомление А.3 со списком получателей по филиалу. */
  pdTransferNotice?: PDTransferNotice | null;
  pdTransferLoading?: boolean;
  pdTransferError?: string | null;
  pdTransferConfirmed?: boolean;
  onPdTransferConfirmedChange?: (checked: boolean) => void;
};

const PAYMENT_RADIO_NAME = 'booking-payment-method';

export function BookingConfirmModal({
  open,
  onClose,
  onConfirm,
  loading,
  slot,
  paymentMode,
  selectedSubscription,
  slotSubs,
  cashAtStudioEnabled,
  serviceNeedsPayment,
  paymentTTLMinutes = 15,
  addonsTotal = 0,
  addonsCount = 0,
  onSelectPaymentMode,
  onSelectSubscription,
  pdTransferNotice,
  pdTransferLoading = false,
  pdTransferError,
  pdTransferConfirmed = false,
  onPdTransferConfirmedChange
}: BookingConfirmModalProps) {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const pdTransferRequired = serviceNeedsPayment && paymentMode === 'online';

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const priceNum = slot.service_price ?? 0;
  const priceLine = priceNum > 0
    ? `${Math.round(priceNum).toLocaleString('ru-RU')} ₽`
    : 'Бесплатно';

  const startTime = (slot.start_time || '').slice(0, 5);
  const endTime = (slot.end_time || '').slice(0, 5);
  const timeLabel = slot.is_all_day ? 'Весь день' : `${startTime} — ${endTime}`;
  const deadlineMin = slot.service_cancellation_deadline_minutes;

  const optionBase =
    'block rounded-xl border-2 p-4 cursor-pointer transition-all active:scale-[0.99] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--focus-ring)] has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-[var(--bg-elevated)]';
  const optionSelected =
    'border-[var(--accent)] bg-[color-mix(in_srgb,var(--accent)_7%,var(--bg-elevated))] shadow-sm';
  const optionDefault =
    'border-transparent bg-[var(--bg-soft)] hover:border-[var(--border)]';

  function subscriptionDeduction(sub: ForSlotSubscription) {
    if (sub.plan_type === 'visits') {
      return (
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          <div className="rounded-lg bg-[var(--bg-elevated)] px-2.5 py-1.5">
            <p className="text-[11px] text-[var(--text-muted)]">Списание</p>
            <p className="mt-0.5 text-sm font-semibold tabular-nums text-[var(--text)]">1 визит</p>
          </div>
          <div className="rounded-lg bg-[var(--bg-elevated)] px-2.5 py-1.5">
            <p className="text-[11px] text-[var(--text-muted)]">Останется</p>
            <p className="mt-0.5 text-sm font-semibold tabular-nums text-[var(--text)]">
              {sub.visits_remaining != null ? Math.max(0, sub.visits_remaining - 1) : '—'} виз.
            </p>
          </div>
        </div>
      );
    }
    if (sub.plan_type === 'certificate') {
      const bal = Number(sub.balance_remaining ?? 0);
      const rest = Math.max(0, bal - priceNum);
      const insufficient = priceNum > 0 && bal < priceNum;
      return (
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          <div className="rounded-lg bg-[var(--bg-elevated)] px-2.5 py-1.5">
            <p className="text-[11px] text-[var(--text-muted)]">Списание</p>
            <p className="mt-0.5 text-sm font-semibold tabular-nums text-[var(--text)]">
              {priceNum > 0 ? `${Math.round(priceNum).toLocaleString('ru-RU')} ₽` : '0 ₽'}
            </p>
          </div>
          <div className="rounded-lg bg-[var(--bg-elevated)] px-2.5 py-1.5">
            <p className="text-[11px] text-[var(--text-muted)]">Останется</p>
            <p className="mt-0.5 text-sm font-semibold tabular-nums text-[var(--text)]">
              {rest.toLocaleString('ru-RU')} ₽
            </p>
          </div>
          {insufficient ? (
            <p className="col-span-2 text-xs font-medium text-[var(--error-text)]">
              Недостаточно средств. Остаток: {bal.toLocaleString('ru-RU')} ₽, стоимость: {priceLine}
            </p>
          ) : null}
        </div>
      );
    }
    return null;
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/55" />
        <Dialog.Content
          className="fixed inset-x-0 bottom-0 z-[101] flex max-h-[90dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-[var(--bg-elevated)] shadow-xl outline-none sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[90vh] sm:w-[calc(100%-2rem)] sm:max-w-sm sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl"
          onOpenAutoFocus={() => {
            previousFocusRef.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const previous = previousFocusRef.current;
            previousFocusRef.current = null;
            if (previous && document.contains(previous)) previous.focus();
          }}
          onInteractOutside={(event) => {
            if (loading) event.preventDefault();
          }}
        >
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5">
            <div className="text-center">
              <Dialog.Title className="text-lg font-semibold tracking-tight text-[var(--text)]">
                Подтверждение записи
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-[var(--text-muted)]">
                {slot.service_name}
              </Dialog.Description>
            </div>

            <div className="mt-4 rounded-xl bg-[var(--bg-soft)] px-4 py-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[var(--text-muted)]">
                  {formatDay(slot.slot_date)}
                </span>
                <span className="shrink-0 font-semibold tabular-nums text-[var(--text)]">
                  {timeLabel}
                </span>
              </div>
              {slot.trainer_name || slot.is_rental ? (
                <p className="mt-1 text-xs text-[var(--text-muted)]">{slot.trainer_name ?? 'Аренда'}</p>
              ) : null}
              {addonsCount > 0 ? (
                <p className="mt-1 text-xs text-[var(--text-muted)]">
                  Допы: {addonsCount} шт.
                  {addonsTotal > 0 ? ` (+${Math.round(addonsTotal).toLocaleString('ru-RU')} ₽)` : ''}
                </p>
              ) : null}
            </div>

            {serviceNeedsPayment ? (
              <fieldset className="m-0 mt-4 min-w-0 space-y-2 border-0 p-0">
                <legend className="mb-2 p-0 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                  Выберите способ оплаты
                </legend>

                {slotSubs.map((sub) => {
                  const isSelected = paymentMode === 'subscription' && selectedSubscription?.id === sub.id;
                  const planKind = sub.plan_type === 'certificate' ? 'Сертификат' : 'Абонемент';
                  return (
                    <label
                      key={sub.id}
                      className={`${optionBase} ${isSelected ? optionSelected : optionDefault}`}
                    >
                      <input
                        type="radio"
                        name={PAYMENT_RADIO_NAME}
                        value={`subscription:${sub.id}`}
                        className="sr-only"
                        aria-label={`${planKind}: ${sub.plan_name}`}
                        checked={isSelected}
                        onChange={() => {
                          onSelectPaymentMode?.('subscription');
                          onSelectSubscription?.(String(sub.id));
                        }}
                      />
                      <span className="flex items-start justify-between gap-2">
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold text-[var(--text)]">{sub.plan_name}</span>
                          <span
                            className={
                              'mt-0.5 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold leading-tight ' +
                              (sub.plan_type === 'certificate'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-sky-100 text-sky-800')
                            }
                          >
                            {planKind}
                          </span>
                        </span>
                        {isSelected ? (
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--accent-bg)] text-xs font-bold text-[var(--accent-fg)]">
                            ✓
                          </span>
                        ) : null}
                      </span>
                      {subscriptionDeduction(sub)}
                      {sub.valid_until ? (
                        <span className="mt-1 block text-[11px] text-[var(--text-muted)]">
                          до {formatValidUntil(sub.valid_until)}
                        </span>
                      ) : null}
                    </label>
                  );
                })}

                <label
                  className={`${optionBase} ${paymentMode === 'online' ? optionSelected : optionDefault}`}
                >
                  <input
                    type="radio"
                    name={PAYMENT_RADIO_NAME}
                    value="online"
                    className="sr-only"
                    aria-label="Оплатить онлайн"
                    checked={paymentMode === 'online'}
                    onChange={() => onSelectPaymentMode?.('online')}
                  />
                  <span className="flex items-start justify-between gap-2">
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-[var(--text)]">Оплатить онлайн</span>
                      <span className="mt-0.5 block text-xs text-[var(--text-muted)]">{priceLine}</span>
                    </span>
                    {paymentMode === 'online' ? (
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--accent-bg)] text-xs font-bold text-[var(--accent-fg)]">
                        ✓
                      </span>
                    ) : null}
                  </span>
                  <span className="mt-1.5 block text-xs leading-relaxed text-[var(--text-muted)]">
                    После подтверждения записи оплатите в течение {formatPaymentTTLMinutes(paymentTTLMinutes)}.
                    Перенаправление на страницу оплаты после подтверждения.
                  </span>
                </label>

                {cashAtStudioEnabled ? (
                  <label
                    className={`${optionBase} ${paymentMode === 'cash' ? optionSelected : optionDefault}`}
                  >
                    <input
                      type="radio"
                      name={PAYMENT_RADIO_NAME}
                      value="cash"
                      className="sr-only"
                      aria-label="В студии"
                      checked={paymentMode === 'cash'}
                      onChange={() => onSelectPaymentMode?.('cash')}
                    />
                    <span className="flex items-start justify-between gap-2">
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-[var(--text)]">В студии</span>
                        <span className="mt-0.5 block text-xs text-[var(--text-muted)]">Наличные или карта</span>
                      </span>
                      {paymentMode === 'cash' ? (
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--accent-bg)] text-xs font-bold text-[var(--accent-fg)]">
                          ✓
                        </span>
                      ) : null}
                    </span>
                    <span className="mt-1.5 block text-xs leading-relaxed text-[var(--text-muted)]">
                      Оплата на ресепшене студии. Администратор отметит в CRM.
                    </span>
                  </label>
                ) : null}
              </fieldset>
            ) : (
              <p className="mt-4 rounded-xl bg-[var(--bg-soft)] px-4 py-3 text-sm text-[var(--text-muted)]">
                Оплата не требуется.
              </p>
            )}

            {pdTransferRequired ? (
              <div className="mt-4 space-y-2" data-testid="pd-transfer-notice">
                {pdTransferNotice ? (
                  <LegalAgreementHtmlBlock html={pdTransferNotice.text} />
                ) : (
                  <p className="rounded-xl bg-[var(--bg-soft)] px-4 py-3 text-xs leading-relaxed text-[var(--text-muted)]">
                    {pdTransferError
                      ? `Не удалось загрузить уведомление о передаче данных: ${pdTransferError}. Обновите страницу.`
                      : 'Загрузка уведомления о передаче данных…'}
                  </p>
                )}
                <label className="flex items-start gap-2 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2.5">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--accent)]"
                    checked={pdTransferConfirmed}
                    disabled={!pdTransferNotice || pdTransferLoading}
                    onChange={(e) => onPdTransferConfirmedChange?.(e.target.checked)}
                    aria-label="Подтверждаю передачу данных для оплаты и кассового чека"
                  />
                  <span className="text-xs leading-snug text-[var(--text)]">
                    Подтверждаю передачу данных для оплаты и кассового чека
                  </span>
                </label>
              </div>
            ) : null}

            <p className="mt-3 text-xs text-[var(--text-muted)]">
              {deadlineMin != null && deadlineMin > 0
                ? `Отменить запись можно не позднее чем за ${formatCancellationLeadMinutes(deadlineMin)} до начала.`
                : 'Бесплатная отмена — по правилам организации.'}
            </p>
          </div>

          <div className="shrink-0 border-t border-[var(--border)] bg-[var(--bg-elevated)] px-5 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 sm:border-t-0 sm:p-0 sm:pb-0">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={onClose} disabled={loading}>
                Отмена
              </Button>
              <Button
                type="button"
                className="w-full sm:w-auto"
                onClick={onConfirm}
                disabled={loading || (pdTransferRequired && !pdTransferConfirmed)}
              >
                {loading ? 'Оформляем…' : pdTransferRequired ? 'Оплатить' : 'Подтвердить запись'}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
